import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  History, RefreshCw, LogIn, Mail, MessageCircle, CalendarPlus,
  CalendarClock, XCircle, CheckCircle2, Shield, Smartphone, MapPin,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 50;

const TYPE_META = {
  login: { label: 'Login', icon: LogIn, cls: 'bg-indigo-100 text-indigo-700' },
  email: { label: 'E-Mail', icon: Mail, cls: 'bg-blue-100 text-blue-700' },
  whatsapp: { label: 'WhatsApp', icon: MessageCircle, cls: 'bg-emerald-100 text-emerald-700' },
  booking: { label: 'Buchung', icon: CalendarPlus, cls: 'bg-amber-100 text-amber-700' },
  reschedule: { label: 'Verschiebung', icon: CalendarClock, cls: 'bg-orange-100 text-orange-700' },
  cancel: { label: 'Storno', icon: XCircle, cls: 'bg-red-100 text-red-700' },
  checkin: { label: 'Check-in', icon: CheckCircle2, cls: 'bg-green-100 text-green-700' },
  admin: { label: 'Admin', icon: Shield, cls: 'bg-gray-200 text-gray-700' },
};

const TABS = [
  { key: '', label: 'Alle' },
  { key: 'login', label: 'Logins' },
  { key: 'email', label: 'E-Mails' },
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'booking', label: 'Buchungen' },
  { key: 'reschedule', label: 'Verschiebungen' },
  { key: 'cancel', label: 'Stornos' },
  { key: 'checkin', label: 'Check-ins' },
  { key: 'admin', label: 'Admin-Aktionen' },
];

const DAY_OPTIONS = [7, 30, 90];

export default function GlobalHistoryPage() {
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [type, setType] = useState('');
  const [days, setDays] = useState(30);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/global-history`, {
        params: {
          type: type || undefined,
          days,
          q: q || undefined,
          limit: PAGE_SIZE,
          skip: page * PAGE_SIZE,
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(res.data.items || []);
      setHasMore(!!res.data.has_more);
    } catch (e) {
      console.error('Globale History laden fehlgeschlagen:', e);
      setItems([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [type, days, q, page]);

  useEffect(() => {
    const t = setTimeout(load, q ? 400 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString('de-CH', {
        timeZone: 'Europe/Zurich',
        day: '2-digit', month: '2-digit', year: '2-digit',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return iso; }
  };

  return (
    <div className="p-6 space-y-5" data-testid="global-history-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <History className="w-6 h-6" /> Globale History
          </h1>
          <p className="text-sm text-gray-500">
            Alle Aktivitäten an einem Ort — Logins, E-Mails, WhatsApp, Buchungen, Stornos, Check-ins &amp; Admin-Aktionen
          </p>
        </div>
        <Button variant="outline" onClick={load} data-testid="global-history-refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Aktualisieren
        </Button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex rounded-lg border overflow-hidden flex-wrap">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`global-history-tab-${t.key || 'all'}`}
              onClick={() => { setType(t.key); setPage(0); }}
              className={`px-3.5 py-2 text-sm font-medium transition-colors ${
                type === t.key ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex rounded-lg border overflow-hidden">
          {DAY_OPTIONS.map((d) => (
            <button
              key={d}
              data-testid={`global-history-days-${d}`}
              onClick={() => { setDays(d); setPage(0); }}
              className={`px-3.5 py-1.5 text-sm font-medium transition-colors ${
                days === d ? 'bg-yellow-400 text-black' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {d} Tage
            </button>
          ))}
        </div>
        <Input
          placeholder="Suche: Name, E-Mail, Betreff, Aktion …"
          value={q}
          onChange={(e) => { setQ(e.target.value); setPage(0); }}
          className="max-w-xs"
          data-testid="global-history-search"
        />
      </div>

      <div className="border rounded-xl overflow-x-auto bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 border-b bg-gray-50">
              <th className="py-2.5 px-4 whitespace-nowrap">Zeit</th>
              <th className="py-2.5 px-4">Typ</th>
              <th className="py-2.5 px-4">Ereignis</th>
              <th className="py-2.5 px-4">Details</th>
              <th className="py-2.5 px-4">Wer / Kunde</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="5" className="py-10 text-center text-gray-400">Lade …</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan="5" className="py-10 text-center text-gray-400" data-testid="global-history-empty">
                Keine Aktivitäten im gewählten Zeitraum.
              </td></tr>
            ) : items.map((e) => {
              const meta = TYPE_META[e.type] || TYPE_META.admin;
              const Icon = meta.icon;
              return (
                <tr key={`${e.type}-${e.id}`} className="border-b last:border-0 hover:bg-gray-50 align-top" data-testid="global-history-row">
                  <td className="py-2.5 px-4 whitespace-nowrap text-gray-600">{fmt(e.ts)}</td>
                  <td className="py-2.5 px-4">
                    <Badge className={`${meta.cls} hover:${meta.cls}`}>
                      <Icon className="w-3 h-3 mr-1" /> {meta.label}
                    </Badge>
                  </td>
                  <td className="py-2.5 px-4 font-medium text-gray-900">
                    {e.title}
                    {e.status === 'failed' && (
                      <Badge className="ml-2 bg-red-100 text-red-700 hover:bg-red-100" title={e.error}>fehlgeschlagen</Badge>
                    )}
                  </td>
                  <td className="py-2.5 px-4 text-gray-600 max-w-md">
                    <span className="block truncate" title={e.description}>{e.description}</span>
                    {(e.old_start || e.new_start) && (
                      <span className="block text-xs text-gray-400">
                        {e.old_start ? fmt(e.old_start) : '—'} → {e.new_start ? fmt(e.new_start) : '—'}
                      </span>
                    )}
                    {e.type === 'login' && (e.device || e.geo_label) && (
                      <span className="flex items-center gap-3 text-xs text-gray-400 mt-0.5" data-testid="global-history-login-meta">
                        {e.device && (
                          <span className="inline-flex items-center gap-1"><Smartphone className="w-3 h-3" />{e.device}</span>
                        )}
                        {e.geo_label && (
                          <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{e.geo_label}</span>
                        )}
                      </span>
                    )}
                  </td>
                  <td className="py-2.5 px-4 text-gray-700">
                    {e.customer_name || e.actor || '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-3 justify-end text-sm">
        <Button variant="outline" size="sm" disabled={page === 0}
          onClick={() => setPage((p) => p - 1)} data-testid="global-history-prev">Zurück</Button>
        <span className="text-gray-500">Seite {page + 1}</span>
        <Button variant="outline" size="sm" disabled={!hasMore}
          onClick={() => setPage((p) => p + 1)} data-testid="global-history-next">Weiter</Button>
      </div>
    </div>
  );
}
